import { useCallback, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { axios } from "@features/config";

import { MANAGEMENT_STYLE } from "@components/Dialog/CreateProjectLandingDialog/SelectManagementStyleDialog";

import { Roundtable } from "@dto/roundtable";

import { RoundtableTypeEnum, RoundtableType } from "@@types/type";

type Response = {
  data: Roundtable[];
  totalItems: number;
};

export interface JoinedProjectsRequest {
  title: string;
  types: RoundtableType[];
  userId: string;
  page: number;
  pageSize: number;
  isClosed?: boolean;
  requireVoteWeight?: boolean;
  managementStyle?: keyof typeof MANAGEMENT_STYLE;
}

const getJoinedProjects = async ({
  title,
  page = 0,
  pageSize = 5,
  types,
  userId,
  isClosed,
  requireVoteWeight,
  managementStyle,
}: Partial<JoinedProjectsRequest>): Promise<Response> => {
  const response = await axios.get(`/round-tables/joined`, {
    params: {
      title,
      pageSize,
      type: types
        ? types.join(",")
        : [RoundtableTypeEnum.FREELANCER, RoundtableTypeEnum.SELF].join(","),
      page: page + 1,
      userId,
      isClosed,
      requireVoteWeight,
      managementStyle,
    },
  });

  return response.data.data;
};

const useGetJoinedProjects = (initParams: Partial<JoinedProjectsRequest>) => {
  const [params, setParams] = useState(initParams);

  const { data, isLoading, isFetching, isSuccess, isError, error, refetch } = useQuery(
    ["getJoinedProject", params],
    () => getJoinedProjects({ ...params })
  );

  useEffect(() => {
    refetch();
  }, [params, refetch]);

  const handleChangeParams = useCallback((params: Partial<JoinedProjectsRequest>) => {
    setParams((prev) => ({ ...prev, ...params }));
  }, []);

  return {
    data: data?.data || [],
    totalItems: data?.totalItems || 0,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    params,
    handleChangeParams,
  };
};

export default useGetJoinedProjects;
